/* eslint-disable react-hooks/exhaustive-deps */
import React, { useReducer, useMemo, useCallback, memo, MutableRefObject } from 'react';
import CatalogDownloadContext from './CatalogDownloadContext';
import initialCatalogDownloadState from './initialCatalogDownloadState';
import catalogDownloadReducer from 'contexts/catalogDownload/reducer/catalogDownload.reducer';
import * as action from 'contexts/catalogDownload/reducer/catalogDownload.actions';
import * as types from 'contexts/catalogDownload/reducer/catalogDownload.types';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import useContextUser from 'hooks/contexts/useContextUser';
import {
  ICatalogFetchCSVDataPlainSkuResponse,
  TCatalogDownloadFiltersSelected,
  TFiltersSelectsValues,
  TImageSelectionTypes,
  imageSelectionTypes,
} from 'interfaces/catalogDownload.interface';
// import useContextMyBrands from 'hooks/contexts/useContextMyBrands';
import { DropResult } from 'react-beautiful-dnd';
import { IChildrenProps } from 'interfaces/general.interface';
import useContextCompany from 'hooks/contexts/useContextCompany';
import { ITableColumnsSelectOrderState } from 'interfaces/tables.interface';

export const catalogDownloadFiltersKeys = {
  brand: 'brand',
  color: 'color',
  season: 'season',
  division: 'division',
  family: 'family',
  gender: 'gender',
  segmentation: 'segmentation',
  price: 'price',
  reference: 'reference',
  material: 'material',
  bill: 'bill',
  order: 'order',
};

function CatalogDownloadProvider(props: IChildrenProps) {
  const [catalogDownloadState, dispatch] = useReducer(
    catalogDownloadReducer,
    initialCatalogDownloadState
  );
  const { getAccessTokenSilently } = useAuth0();
  const { t: translate } = useTranslation();
  const { dbUser } = useContextUser();
  const { company } = useContextCompany();

  // Filters //////////////////
  const getFilters = useCallback(async () => {
    if (!company?.brand.id || !company?.country?.id || !dbUser?.i18n_lang_id)
      return false;
    const token = await getAccessTokenSilently();
    if (!token) return false;
    return await action.getFiltersAction({ dispatch, company, dbUser, token, translate });
  }, [company?.brand.id, company?.country?.id, dbUser?.i18n_lang_id]);

  const setSelectedValues = useCallback((value: React.Key[]) => {
    dispatch({ type: types.SET_SELECTED_VALUES, payload: value });
  }, []);

  const applyFilterChanges = useCallback(
    async (value: TCatalogDownloadFiltersSelected | null) => {
      if (!company) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.applyFilterChangesAction({
        dispatch,
        company,
        value,
        catalogDownloadState,
        token,
        translate,
      });
    },
    [catalogDownloadState, company]
  );

  const cancelFilters = useCallback(() => {
    dispatch({
      type: types.SET_FILTERS_SELECTED_TEMP,
      payload: catalogDownloadState.filtersSelected,
    });
    dispatch({ type: types.SET_IS_OPEN_FILTERS, payload: false });
  }, [catalogDownloadState.filtersSelected]);

  const cleanFilters = useCallback(() => {
    dispatch({
      type: types.SET_FILTERS_SELECTED_TEMP,
      payload: null,
    });
    dispatch({ type: types.SET_IS_OPEN_FILTERS, payload: false });
    dispatch({
      type: types.SET_FILTERS_SELECTED,
      payload: null,
    });
    dispatch({ type: types.SET_SELECTED_VALUES, payload: [] });
    dispatch({ type: types.SET_FILTERED_LIST, payload: [] });
    dispatch({ type: types.SET_FILTERS_TAGS, payload: [] });
    dispatch({ type: types.SET_IMAGE_SELECTION, payload: imageSelectionTypes.all });
    dispatch({ type: types.SET_PVI_SLIDER, payload: [] });
    dispatch({ type: types.SET_PVPR_SLIDER, payload: [] });
    dispatch({ type: types.SET_FAVORITES, payload: '' });
  }, []);

  const onChangeFilterSelect = useCallback(
    (filterName: string, value: TFiltersSelectsValues[]) => {
      dispatch({
        type: types.SET_FILTERS_SELECTED_TEMP,
        payload: {
          ...catalogDownloadState.filtersSelectedTemp,
          [filterName]: value,
        },
      });
    },
    [catalogDownloadState.filtersSelectedTemp]
  );

  const openFilters = useCallback((value: boolean) => {
    dispatch({ type: types.SET_IS_OPEN_FILTERS, payload: value });
  }, []);

  const setImageSelection = useCallback((value: TImageSelectionTypes) => {
    dispatch({ type: types.SET_IMAGE_SELECTION, payload: value });
  }, []);

  const setFavorites = useCallback(
    (value: boolean) => {
      dispatch({ type: types.SET_FAVORITES, payload: value === true ? dbUser?.id : '' });
    },
    [dbUser?.id]
  );

  const setFilteredList = useCallback((value: ICatalogFetchCSVDataPlainSkuResponse[]) => {
    dispatch({ type: types.SET_FILTERED_LIST, payload: value });
  }, []);

  const setPviSlider = useCallback((value: [number, number]) => {
    dispatch({
      type: types.SET_PVI_SLIDER,
      payload: value[0] === 0 && value[1] === 0 ? [] : value,
    });
  }, []);

  const setPvprSlider = useCallback((value: [number, number]) => {
    dispatch({
      type: types.SET_PVPR_SLIDER,
      payload: value[0] === 0 && value[1] === 0 ? [] : value,
    });
  }, []);

  // Columns //////////////////
  const moveColumnsOrder = useCallback(
    (result: DropResult) => {
      return action.moveColumnsOrderAction({
        dispatch,
        result,
        columnsOrder: catalogDownloadState.columnsOrder,
      });
    },
    [catalogDownloadState.columnsOrder]
  );

  const checkColumn = useCallback(
    (columnKey: string) => {
      return action.checkColumnAction({
        dispatch,
        columnKey,
        columnsOrder: catalogDownloadState.columnsOrder,
      });
    },
    [catalogDownloadState.columnsOrder]
  );

  // Search //////////////////
  const setQuerySearch = useCallback(async (value: string) => {
    dispatch({ type: types.SET_QUERY_SEARCH, payload: value });
  }, []);

  const applySearch = useCallback(async () => {
    if (!company?.brand.id) return false;
    const token = await getAccessTokenSilently();
    if (!token) return false;
    return action.applySearchAction({
      dispatch,
      catalogDownloadState,
      brandId: company.brand.id,
      translate,
      token,
    });
  }, [catalogDownloadState, company?.brand.id]);

  // Download CSV //////////////////
  const downloadCatalog = useCallback(
    async ({ completedDownloads }: { completedDownloads: MutableRefObject<number> }) => {
      if (!company?.brand?.id) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.donwloadCatalogAction({
        dispatch,
        catalogDownloadState,
        completedDownloads,
        brandId: company.brand.id,
        translate,
        token,
      });
    },
    [catalogDownloadState, company?.brand?.id]
  );

  const cancelDownloadCatalog = useCallback(
    async ({ completedDownloads }: { completedDownloads: MutableRefObject<number> }) => {
      return await action.cancelDownloadCatalogAction({
        dispatch,
        catalogDownloadState,
        completedDownloads,
      });
    },
    [catalogDownloadState]
  );

  const setDownloadImages = useCallback(
    async (value: boolean, columns: Record<string, ITableColumnsSelectOrderState>) => {
      return await action.setDownloadImagesAction({ dispatch, value, columns });
    },
    []
  );

  const handlePagination = useCallback(
    async ({ page, pageSize }: { page: number; pageSize: number }) => {
      if (!company?.brand.id) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.handlePaginationAction({
        dispatch,
        page,
        pageSize,
        catalogDownloadState,
        brandId: company.brand.id,
        token,
      });
    },
    [catalogDownloadState, company?.brand.id]
  );

  const memoProvider = useMemo(
    () => ({
      ...catalogDownloadState,
      // Filters
      getFilters,
      setFilteredList,
      setSelectedValues,
      openFilters,
      cancelFilters,
      cleanFilters,
      applyFilterChanges,
      onChangeFilterSelect,
      setImageSelection,
      setFavorites,
      setPviSlider,
      setPvprSlider,
      // Columns
      moveColumnsOrder,
      checkColumn,
      // Search
      setQuerySearch,
      applySearch,
      // Download
      downloadCatalog,
      cancelDownloadCatalog,
      setDownloadImages,
      handlePagination,
    }),
    [
      catalogDownloadState,
      // Filters
      getFilters,
      setFilteredList,
      setSelectedValues,
      openFilters,
      cancelFilters,
      cleanFilters,
      applyFilterChanges,
      onChangeFilterSelect,
      setImageSelection,
      setFavorites,
      setPviSlider,
      setPvprSlider,
      // Columns
      moveColumnsOrder,
      checkColumn,
      // Search
      setQuerySearch,
      applySearch,
      // Download
      downloadCatalog,
      cancelDownloadCatalog,
      setDownloadImages,
      handlePagination,
    ]
  );

  return (
    <CatalogDownloadContext.Provider value={memoProvider}>
      {props.children}
    </CatalogDownloadContext.Provider>
  );
}

export default memo(CatalogDownloadProvider);
